(() => {
  const ROOT_SELECTOR = "[data-sticky-anchor]";
  const LINK_SELECTOR = "[data-sa-link]";
  const CTA_SELECTOR  = "[data-sa-cta]";

  const HEADER_SELECTORS = [
    "#shopify-section-header",
    "#shopify-section-header-group",
    "header",
    ".hdt-header",
    ".header__wrapper",
    ".site-header",
  ];

  const px = (n) => `${Math.round(n)}px`;

  function getVarPx(el, name, fallback = 0) {
    const v = getComputedStyle(el).getPropertyValue(name).trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function findHeaderEl() {
    for (const sel of HEADER_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function getHeaderVisibleHeight(headerEl) {
    if (!headerEl) return 0;
    const rect = headerEl.getBoundingClientRect();
    const h = rect.height || headerEl.offsetHeight || 0;
    if (h <= 0) return 0;
    if (rect.top > 1) return 0;
    return Math.max(0, Math.min(h, rect.bottom));
  }

  function getBarHeight(stickyEl) {
    const wrap = stickyEl.querySelector(".sticky-anchor__wrapper");
    return wrap ? wrap.offsetHeight : stickyEl.offsetHeight;
  }

  function ensurePlaceholder(stickyEl) {
    const outer = stickyEl.closest(".sticky-anchor-outer") || stickyEl.parentElement;
    if (!outer) return null;
    let ph = outer.querySelector(".sticky-anchor__placeholder");
    if (!ph) {
      ph = document.createElement("div");
      ph.className = "sticky-anchor__placeholder";
      ph.style.height = "0px";
      outer.insertBefore(ph, stickyEl);
    }
    return ph;
  }

  function getTargetElementFromHash(hash) {
    if (!hash || !hash.startsWith("#")) return null;
    const id = decodeURIComponent(hash.slice(1));
    return document.getElementById(id);
  }

  function smoothScrollToTarget(targetEl, offset) {
    const y = window.scrollY + targetEl.getBoundingClientRect().top - offset - 8;
    window.scrollTo({ top: y, behavior: "smooth" });
  }

  // debounce 工具，用于 resize 事件
  function debounce(fn, delay = 150) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  function initOneStickyAnchor(stickyEl) {
    const linksWrap = stickyEl.querySelector("[data-sa-links]");
    const links     = Array.from(stickyEl.querySelectorAll(LINK_SELECTOR));
    const ctaEl     = stickyEl.querySelector(CTA_SELECTOR);

    if (!linksWrap) return;

    const placeholder = ensurePlaceholder(stickyEl);
    if (!placeholder) return;

    const headerEl = findHeaderEl();

    // ── 尺寸同步 ──────────────────────────────────────────
    const syncBarCssVar = () => {
      stickyEl.style.setProperty("--sa-bar-h", px(getBarHeight(stickyEl)));
    };

    const syncDynTop = () => {
      const baseTop   = getVarPx(stickyEl, "--sa-top", 0);
      const headerTop = getHeaderVisibleHeight(headerEl);
      const dynTop    = baseTop + headerTop;
      stickyEl.style.setProperty("--sa-dyn-top", px(dynTop));
      return dynTop;
    };

    // ── Fixed 状态切换 ────────────────────────────────────
    const applyFixed = () => {
      syncBarCssVar();
      const dynTop = syncDynTop();
      const barH   = getBarHeight(stickyEl);
      const phRect = placeholder.getBoundingClientRect();

      if (phRect.top <= dynTop) {
        stickyEl.classList.add("is-fixed");
        placeholder.style.height = px(barH);
      } else {
        stickyEl.classList.remove("is-fixed");
        placeholder.style.height = "0px";
      }
    };

    applyFixed();

    // scroll：rAF 节流
    let ticking = false;
    window.addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { applyFixed(); ticking = false; });
    }, { passive: true });

    // resize：debounce
    window.addEventListener("resize", debounce(applyFixed), { passive: true });
    window.addEventListener("load",   () => { syncBarCssVar(); applyFixed(); }, { passive: true });

    // ── 点击处理（anchor 链接 + CTA 统一入口）────────────
    stickyEl.addEventListener("click", (e) => {

      // 1. Shop Now CTA
      const cta = e.target.closest(CTA_SELECTOR);
      if (cta) {
        const href = cta.getAttribute("href");
        if (href && href !== "" && href !== "#") {
          // 有 URL：正常跳转，不拦截
          return;
        }
        // 无 URL：平滑回顶
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      // 2. 普通 anchor 链接
      const a = e.target.closest(LINK_SELECTOR);
      if (!a) return;
      const sel = a.getAttribute("data-target") || a.getAttribute("href");
      if (!sel || !sel.startsWith("#")) return;
      const targetEl = getTargetElementFromHash(sel);
      if (!targetEl) return;

      e.preventDefault();
      const dynTop = syncDynTop();
      const offset = dynTop + getBarHeight(stickyEl);
      smoothScrollToTarget(targetEl, offset);
    });

    // ── 滚动高亮 ──────────────────────────────────────────
    if (links.length === 0) return;

    const targets = links
      .map((a) => {
        const sel = a.getAttribute("data-target") || a.getAttribute("href");
        const el  = getTargetElementFromHash(sel);
        return el ? { link: a, target: el } : null;
      })
      .filter(Boolean);

    if (targets.length === 0) return;

    const setActive = (activeLink) => {
      links.forEach((l) => l.classList.toggle("is-active", l === activeLink));
      if (activeLink) {
        activeLink.scrollIntoView({ block: "nearest", inline: "center" });
      }
    };

    setActive(links[0]);

    const makeObserver = () => {
      const dynTop    = syncDynTop();
      const spyOffset = dynTop + getBarHeight(stickyEl);
      return new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((en) => en.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            const hit = targets.find((t) => t.target === visible[0].target);
            if (hit) setActive(hit.link);
          }
        },
        { rootMargin: `-${spyOffset + 12}px 0px -70% 0px`, threshold: [0.01, 0.2, 0.5] }
      );
    };

    let io = makeObserver();
    targets.forEach((t) => io.observe(t.target));

    // resize 时重建 observer（debounce）
    window.addEventListener("resize", debounce(() => {
      io.disconnect();
      io = makeObserver();
      targets.forEach((t) => io.observe(t.target));
    }), { passive: true });
  }

  // ── 启动 ──────────────────────────────────────────────
  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initOneStickyAnchor);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

})();
